import { useState } from 'react';
import { Card, Col, Row, Segmented, Space, Table, Tag, Typography, Empty } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Area, Column, Pie } from '@ant-design/plots';
import { api } from '../../api/client';
import { ListenerName } from '../../lib/listenerName';
import { CallRequestStatsCard } from './CallRequestStatsCard';
import { PeriodBreakdown } from './PeriodBreakdown';
import { formatCoins } from '../../lib/money';
import {
  coinSalesColumnData,
  giftPieData,
  type CoinSalesBucket,
  type GiftRevenueRow,
} from './coinAnalytics';
import { usePermissions } from '../../auth/store';

interface CallBucket {
  bucket: string;
  total: number;
  connected: number;
  ended_normal: number;
  ended_dropped: number;
  ended_flagged: number;
  ended_rejected: number;
  ended_low_balance: number;
}
interface RevenueBucket {
  bucket: string;
  platformFeePaise: string;
  rechargePaise: string;
  refundPaise: string;
}
interface IncidentBucket {
  bucket: string;
  fatal: number;
  error: number;
  warning: number;
  info: number;
}
interface TopListener {
  listenerId: string;
  displayName: string;
  handle: string | null;
  tier: string;
  value: string;
}
interface PackageSalesRow {
  packageId: string | null;
  packageName: string;
  orders: number;
  cashPaise: string;
  /** Paid coins sold (snapshot at purchase) — shown as-is, never derived. */
  coinsSold?: number | null;
  bonusCoins: number;
}

function rupees(paise: string | number): string {
  const n = typeof paise === 'string' ? BigInt(paise) : BigInt(paise);
  return `₹${(Number(n) / 100).toLocaleString('en-IN')}`;
}

function hourLabel(iso: string): string {
  const d = new Date(iso);
  return `${d.getHours().toString().padStart(2, '0')}:00`;
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

export function AnalyticsPage() {
  // Window selectors. Calls/incidents are hourly; revenue is daily. Failed
  // calls + mode mix share the calls window selector for consistency.
  const [hours, setHours] = useState<number>(24);
  const [days, setDays] = useState<number>(14);
  const [topMetric, setTopMetric] = useState<'earnings' | 'calls'>('earnings');

  // Revenue and the earnings leaderboard are separate permissions from the
  // page itself: an ops operator can read call volume without seeing money.
  const { can } = usePermissions();
  const canRevenue = can('analytics.revenue.view');
  const canTopListeners = can('analytics.listeners.view');

  const calls = useQuery({
    queryKey: ['analytics', 'calls', hours],
    queryFn: async () =>
      (await api.get<CallBucket[]>('/admin/analytics/calls-hourly', { params: { hours } })).data,
    refetchInterval: 30_000,
  });
  const revenue = useQuery({
    queryKey: ['analytics', 'revenue', days],
    queryFn: async () =>
      (await api.get<RevenueBucket[]>('/admin/analytics/revenue-daily', { params: { days } })).data,
    enabled: canRevenue,
    refetchInterval: 60_000,
  });
  const failedBreakdown = useQuery({
    queryKey: ['analytics', 'failed', hours],
    queryFn: async () =>
      (await api.get<Array<{ status: string; count: number }>>(
        '/admin/analytics/failed-calls-breakdown',
        { params: { hours } },
      )).data,
    refetchInterval: 30_000,
  });
  const modeMix = useQuery({
    queryKey: ['analytics', 'mode-mix', hours],
    queryFn: async () =>
      (await api.get<Array<{ mode: string; count: number }>>(
        '/admin/analytics/call-mode-mix',
        { params: { hours } },
      )).data,
    refetchInterval: 30_000,
  });
  const incidents = useQuery({
    queryKey: ['analytics', 'incidents', hours],
    queryFn: async () =>
      (await api.get<IncidentBucket[]>('/admin/analytics/incidents-hourly', { params: { hours } })).data,
    refetchInterval: 30_000,
  });
  const topListeners = useQuery({
    queryKey: ['analytics', 'top-listeners', topMetric, days],
    queryFn: async () =>
      (await api.get<TopListener[]>('/admin/analytics/top-listeners', {
        params: { metric: topMetric, days, limit: 10 },
      })).data,
    enabled: canTopListeners,
    refetchInterval: 60_000,
  });
  const funnel = useQuery({
    queryKey: ['analytics', 'listener-funnel'],
    queryFn: async () =>
      (await api.get<Record<string, number>>('/admin/analytics/listener-funnel')).data,
    refetchInterval: 60_000,
  });
  const coinSales = useQuery({
    queryKey: ['analytics', 'coin-sales', days],
    queryFn: async () =>
      (await api.get<CoinSalesBucket[]>('/admin/analytics/coin-sales-daily', { params: { days } })).data,
    enabled: canRevenue,
    refetchInterval: 60_000,
  });
  const giftRevenue = useQuery({
    queryKey: ['analytics', 'gift-revenue', days],
    queryFn: async () =>
      (await api.get<GiftRevenueRow[]>('/admin/analytics/gift-revenue', { params: { days } })).data,
    enabled: canRevenue,
    refetchInterval: 60_000,
  });
  const packageSales = useQuery({
    queryKey: ['analytics', 'package-sales', days],
    queryFn: async () =>
      (await api.get<PackageSalesRow[]>('/admin/analytics/package-sales', { params: { days } })).data,
    enabled: canRevenue,
    refetchInterval: 60_000,
  });

  // ----- Chart data transforms -----
  // Stacked area: total = sum of connected + ended_normal + ended_dropped + …
  // We flatten the time-series into one row per (bucket, status) so the
  // Area chart can stack by `status`.
  const callsArea = (calls.data ?? []).flatMap((b) => [
    { time: hourLabel(b.bucket), status: 'normal', value: b.ended_normal + b.connected },
    { time: hourLabel(b.bucket), status: 'low-balance', value: b.ended_low_balance },
    { time: hourLabel(b.bucket), status: 'dropped', value: b.ended_dropped },
    { time: hourLabel(b.bucket), status: 'rejected', value: b.ended_rejected },
    { time: hourLabel(b.bucket), status: 'flagged', value: b.ended_flagged },
  ]);

  const revenueArea = (revenue.data ?? []).flatMap((b) => [
    { day: dayLabel(b.bucket), kind: 'platform fee', paise: Number(b.platformFeePaise) },
    { day: dayLabel(b.bucket), kind: 'recharges', paise: Number(b.rechargePaise) },
  ]);

  const incidentsArea = (incidents.data ?? []).flatMap((b) => [
    { time: hourLabel(b.bucket), severity: 'fatal', value: b.fatal },
    { time: hourLabel(b.bucket), severity: 'error', value: b.error },
    { time: hourLabel(b.bucket), severity: 'warning', value: b.warning },
    { time: hourLabel(b.bucket), severity: 'info', value: b.info },
  ]);

  // Coins straight from the API — no paise / coin-rate derivation.
  const coinSalesColumn = coinSalesColumnData(coinSales.data ?? [], dayLabel);
  const giftPie = giftPieData(giftRevenue.data ?? []);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
          Analytics
          <Tag color="success" style={{ marginLeft: 12 }}>
            30s
          </Tag>
        </Typography.Title>
        <Space>
          <Typography.Text type="secondary">Hourly window</Typography.Text>
          <Segmented<number>
            value={hours}
            onChange={(v) => setHours(v as number)}
            options={[
              { label: '6h', value: 6 },
              { label: '24h', value: 24 },
              { label: '3d', value: 72 },
              { label: '7d', value: 168 },
            ]}
          />
        </Space>
      </Space>
      {/* One chosen period: calls, listener performance, money. */}
      <PeriodBreakdown />
      <CallRequestStatsCard days={days} />

      {/* Row 1: call volume timeline (stacked area) + failed-call breakdown pie */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Call volume by outcome">
            {callsArea.length === 0 ? (
              <Empty description="No call activity in this window." />
            ) : (
              <Area
                data={callsArea}
                xField="time"
                yField="value"
                stack
                colorField="status"
                height={280}
                legend={{ color: { position: 'top' } }}
                scale={{
                  color: {
                    range: ['#10B981', '#FFD06A', '#F59E0B', '#9CA3AF', '#EF4444'],
                  },
                }}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Failed calls breakdown">
            {(failedBreakdown.data ?? []).length === 0 ? (
              <Empty description="No failed calls in this window." />
            ) : (
              <Pie
                data={failedBreakdown.data ?? []}
                angleField="count"
                colorField="status"
                height={280}
                radius={0.9}
                innerRadius={0.6}
                label={{
                  text: 'count',
                  position: 'spider',
                  fontSize: 11,
                }}
                legend={{ color: { position: 'bottom' } }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Row 2: revenue (daily) + call-mode mix donut */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={canRevenue ? 16 : 24}>
          {canRevenue ? (
          <Card
            title="Revenue trend"
            extra={
              <Segmented<number>
                value={days}
                onChange={(v) => setDays(v as number)}
                options={[
                  { label: '7d', value: 7 },
                  { label: '14d', value: 14 },
                  { label: '30d', value: 30 },
                  { label: '90d', value: 90 },
                ]}
                size="small"
              />
            }
          >
            {revenueArea.length === 0 ? (
              <Empty description="No money has moved in this window." />
            ) : (
              <Column
                data={revenueArea}
                xField="day"
                yField="paise"
                colorField="kind"
                group
                height={280}
                axis={{
                  y: {
                    labelFormatter: (v: number) => rupees(v),
                  },
                }}
                tooltip={{
                  items: [
                    {
                      channel: 'y',
                      valueFormatter: (v: number) => rupees(v),
                    },
                  ],
                }}
                legend={{ color: { position: 'top' } }}
                scale={{ color: { range: ['#FB3C7F', '#10B981'] } }}
              />
            )}
          </Card>
          ) : null}
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Audio vs video mix">
            {(modeMix.data ?? []).length === 0 ? (
              <Empty description="No calls in this window." />
            ) : (
              <Pie
                data={modeMix.data ?? []}
                angleField="count"
                colorField="mode"
                height={280}
                radius={0.9}
                innerRadius={0.6}
                label={{ text: 'count' }}
                legend={{ color: { position: 'bottom' } }}
                scale={{ color: { range: ['#FB3C7F', '#FFD06A'] } }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Coin economy: coin sales (paid vs bonus) + gift revenue by type.
          Money surfaces — hidden without analytics.revenue.view. */}
      {canRevenue && (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            title="Coin sales — paid vs bonus"
            extra={
              <Segmented<number>
                value={days}
                onChange={(v) => setDays(v as number)}
                options={[
                  { label: '7d', value: 7 },
                  { label: '14d', value: 14 },
                  { label: '30d', value: 30 },
                  { label: '90d', value: 90 },
                ]}
                size="small"
              />
            }
          >
            {coinSalesColumn.length === 0 ? (
              <Empty description="No coins sold in this window." />
            ) : (
              <Column
                data={coinSalesColumn}
                xField="day"
                yField="coins"
                colorField="kind"
                stack
                height={280}
                axis={{ y: { labelFormatter: (v: number) => `${v.toLocaleString('en-IN')}` } }}
                tooltip={{
                  items: [
                    {
                      channel: 'y',
                      valueFormatter: (v: number) => `${v.toLocaleString('en-IN')} coins`,
                    },
                  ],
                }}
                legend={{ color: { position: 'top' } }}
                scale={{
                  color: {
                    domain: ['paid coins', 'bonus coins'],
                    range: ['#FFD06A', '#22A06B'],
                  },
                }}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Gift revenue by type">
            {giftPie.data.length === 0 ? (
              <Empty description="No gifts sent in this window." />
            ) : (
              <Pie
                data={giftPie.data}
                angleField="value"
                colorField="gift"
                height={280}
                radius={0.9}
                innerRadius={0.6}
                label={{ text: 'value' }}
                tooltip={{
                  items: [
                    {
                      channel: 'y',
                      valueFormatter: (v: number) =>
                        giftPie.unit === 'coins'
                          ? formatCoins(v)
                          : `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
                    },
                  ],
                }}
                legend={{ color: { position: 'bottom' } }}
              />
            )}
          </Card>
        </Col>
      </Row>

      )}

      {/* Package performance: which packages sell + their bonus cost */}
      {canRevenue && (
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card title="Package performance (captured recharges)">
            <Table<PackageSalesRow>
              rowKey={(r) => r.packageId ?? 'custom'}
              loading={packageSales.isLoading}
              dataSource={packageSales.data ?? []}
              pagination={false}
              size="small"
              locale={{ emptyText: 'No recharges captured in this window.' }}
              columns={[
                {
                  title: 'Package',
                  dataIndex: 'packageName',
                  render: (n: string) => <span style={{ fontWeight: 600 }}>{n}</span>,
                },
                { title: 'Orders', dataIndex: 'orders', align: 'right', width: 110 },
                {
                  title: 'Cash collected',
                  key: 'cash',
                  dataIndex: 'cashPaise',
                  align: 'right',
                  width: 170,
                  render: (p: string) => rupees(p),
                },
                {
                  title: 'Coins sold',
                  key: 'coins',
                  dataIndex: 'coinsSold',
                  align: 'right',
                  width: 170,
                  render: (c: number | null | undefined) =>
                    typeof c === 'number' ? formatCoins(c) : '—',
                },
                {
                  title: 'Bonus granted',
                  dataIndex: 'bonusCoins',
                  align: 'right',
                  width: 170,
                  render: (c: number) => `${c.toLocaleString('en-IN')} coins`,
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
      )}

      {/* Row 3: app health timeline */}
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card title="App incidents over time">
            {incidentsArea.length === 0 ? (
              <Empty description="No client incidents in this window. Apps are happy." />
            ) : (
              <Area
                data={incidentsArea}
                xField="time"
                yField="value"
                colorField="severity"
                stack
                height={220}
                legend={{ color: { position: 'top' } }}
                scale={{
                  color: {
                    domain: ['fatal', 'error', 'warning', 'info'],
                    range: ['#FB3C7F', '#EF4444', '#FFD06A', '#9CA3AF'],
                  },
                }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Row 4: leaderboard + funnel */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={canTopListeners ? 16 : 24}>
          {canTopListeners ? (
          <Card
            title="Top listeners"
            extra={
              <Segmented<'earnings' | 'calls'>
                value={topMetric}
                onChange={(v) => setTopMetric(v as 'earnings' | 'calls')}
                options={[
                  { label: 'Earnings', value: 'earnings' },
                  { label: 'Calls completed', value: 'calls' },
                ]}
                size="small"
              />
            }
          >
            <Table<TopListener>
              rowKey="listenerId"
              loading={topListeners.isLoading}
              dataSource={topListeners.data ?? []}
              pagination={false}
              size="small"
              columns={[
                {
                  title: '#',
                  width: 50,
                  render: (_: unknown, _r: TopListener, i: number) => i + 1,
                },
                {
                  title: 'Listener',
                  render: (_: unknown, r: TopListener) => (
                    <ListenerName
                      listener={{ displayName: r.displayName, handle: r.handle, id: r.listenerId }}
                      listenerId={r.listenerId}
                      secondary="inline"
                    />
                  ),
                },
                {
                  title: 'Listener level',
                  dataIndex: 'tier',
                  render: (t: string) => (
                    <Tag
                      color={t === 'clinical' ? 'gold' : t === 'coach' ? 'magenta' : 'blue'}
                    >
                      {t}
                    </Tag>
                  ),
                  width: 110,
                },
                {
                  title: topMetric === 'earnings' ? `Earnings (last ${days}d)` : `Calls (last ${days}d)`,
                  dataIndex: 'value',
                  width: 200,
                  render: (v: string) => (topMetric === 'earnings' ? rupees(v) : v),
                  align: 'right',
                },
              ]}
            />
          </Card>
          ) : null}
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Listener funnel">
            {funnel.data ? (
              <Column
                data={[
                  { stage: 'pending', count: funnel.data.pending ?? 0 },
                  { stage: 'interview', count: funnel.data.interview_scheduled ?? 0 },
                  { stage: 'approved', count: funnel.data.approved ?? 0 },
                  { stage: 'rejected', count: funnel.data.rejected ?? 0 },
                  { stage: 'suspended', count: funnel.data.suspended ?? 0 },
                ]}
                xField="stage"
                yField="count"
                height={280}
                colorField="stage"
                scale={{
                  color: {
                    domain: ['pending', 'interview', 'approved', 'rejected', 'suspended'],
                    range: ['#FFD06A', '#9CA3AF', '#10B981', '#EF4444', '#7C3AED'],
                  },
                }}
                label={{ text: 'count', position: 'top' }}
                legend={false}
              />
            ) : (
              <Empty />
            )}
          </Card>
        </Col>
      </Row>
    </Space>
  );
}
